import { useMemo, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { BarChart, LineChart } from "@mui/x-charts";
import { useMediaQuery } from "@mui/material"; 
import CostSummaryToggle from "../components/common/CostSummarytoggle";

/* ================= COLORS ================= */
const COLORS = {
  mortgagePaid: "#7B7BCF",
  rentPaid: "#2ECC71",
  outstanding: "#FF3B5C",
};

/* ================= EMI ================= */
const calculateEMI = (loan, rate, years) => {
  const r = rate / (12 * 100);
  const n = years * 12;
  return (loan * r) / (1 - Math.pow(1 + r, -n));
};

export default function BuyVsRentPage() {
  const [price, setPrice] = useState(1000000);
  const [yearlyRent, setYearlyRent] = useState(100000);
  const [interest, setInterest] = useState(5);
  const [downPayment, setDownPayment] = useState(100000);
  const [loanYears, setLoanYears] = useState(25);
  const [stayYears, setStayYears] = useState(5);
  const isMobile = useMediaQuery("(max-width:576px)");
  const [visible, setVisible] = useState({
    mortgagePaid: true,
    rentPaid: true,
    outstanding: true,
  });

  /* ================= CALCULATION ================= */
  const calc = useMemo(() => {
    const loan = price - downPayment;
    const emi = calculateEMI(loan, interest, loanYears);
    const rentMonthly = yearlyRent / 12;

    const yearlyMortgage = emi * 12;
    const totalMortgage = yearlyMortgage * loanYears;

    const mortgagePaid = [];
    const outstanding = [];
    const rentPaid = [];

    for (let i = 0; i <= loanYears; i++) {
      mortgagePaid.push(yearlyMortgage * i);
      outstanding.push(totalMortgage - yearlyMortgage * i);
      rentPaid.push(i <= stayYears ? rentMonthly * 12 * i : null);
    }

    const cheaperText =
      rentMonthly > emi
        ? `Buying is ${((1 - emi / rentMonthly) * 100).toFixed(
            2
          )}% cheaper than renting on a monthly basis`
        : `Renting is ${(
            (1 - rentMonthly / emi) *
            100
          ).toFixed(2)}% cheaper than buying on a monthly basis`;

    return {
      emi,
      rentMonthly,
      yearlyMortgage,
      totalMortgage,
      mortgagePaid,
      rentPaid,
      outstanding,
      cheaperText,
    };
  }, [
    price,
    yearlyRent,
    interest,
    downPayment,
    loanYears,
    stayYears,
  ]);

  return (
    
    <Container className="my-5 buy-vs-rent-page">
      <style>
  {
    `
    .small-input
    {
       height: 30px;
    min-height: 20px;
    width: 30%;
     border:none;
     border-bottom:1px solid gray;
     border-radius:0px;
     padding:6px 4px 6px ;

    }
    .amount-enter-wrap
    {
    display:flex;
    justify-content:end;
    }
    `
  }
</style>
      {/* ================= INPUT ================= */}
      <Row className="mb-4">
        <Col>
          <div className="card p-4 rounded-3">
            <h4 className="fw-semibold mb-3">
              Buy vs Rent Calculator
            </h4>

            <Row className="g-4">
              <Col md={6}>
                <label className="form-label">
                  Property Price
                </label>
                <input
                  className="form-control"
                  value={price}
                  onChange={(e) => setPrice(+e.target.value)}
                />

                <label className="form-label mt-3">
                  Yearly Rent
                </label>
                <input
                  className="form-control"
                  value={yearlyRent}
                  onChange={(e) => setYearlyRent(+e.target.value)}
                />

                <label className="form-label mt-3">
                  Interest Rate (%)
                </label>
                <input
                  className="form-control"
                  value={interest}
                  onChange={(e) => setInterest(+e.target.value)}
                />
              </Col>

              <Col md={6}>
                <label className="form-label">
                  Duration of Residency ({stayYears} Years)
                </label>
                <input
                  type="range"
                  className="form-range mb-5"
                   min="1"
                  max={loanYears}
                  value={stayYears}
                  onChange={(e) =>
                    setStayYears(+e.target.value)
                  }
                />

                <label className="form-label d-flex justify-content-between align-items-center">
                  Down Payment (Rs{" "}
                  {downPayment.toLocaleString()})
                  <div className="text-dark amount-enter-wrap d-flex align-items-center">
                  <span className="text-dark">
                    Enter Amount:
                  </span>
                  <input
                  type="text"
                  className="form-control small-input"
                  value={downPayment}
                   onChange={(e) => setDownPayment(Number(e.target.value))}
                   placeholder="Enter "
                  />
                  </div>
                </label>
                <input
                  type="range"
                  className="form-range mb-5"
                  min="0"
                  max={price}
                  value={downPayment}
                  onChange={(e) =>
                    setDownPayment(+e.target.value)
                  }
                />

                <label className="form-label">
                  Loan Duration ({loanYears} Years)
                </label>
                <input
                  type="range"
                  className="form-range mb-5"
                  min="5"
                  max="30"
                  value={loanYears}
                  onChange={(e) =>
                    setLoanYears(+e.target.value)
                  }
                />
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      {/* ================= RESULT STRIP ================= */}
      <Row className="mb-4">
        <Col>
          <div className="card p-3 text-center bg-light rounded-3">
         <span className="result-summary-text"> <strong>{calc.cheaperText}</strong> </span>  
          </div>
        </Col>
      </Row>

      {/* ================= GRAPHS ================= */}
      <Row className="g-4">
        <Col md={6}>
          <div className="card p-4 rounded-3 bar-graph">
            <BarChart
              height={300}
              xAxis={[{ scaleType: "band", data: ["Monthly"] }]}
              series={[
                {
                  label: "Rent Monthly Cost",
                  data: [calc.rentMonthly],
                  color: COLORS.rentPaid,
                },
                {
                  label: "Buy Monthly Cost",
                  data: [calc.emi],
                  color: COLORS.mortgagePaid,
                },
              ]}
            />
          </div>
        </Col>

        <Col md={6}>
          <div className="card p-4 rounded-3 line-graph">
            {/* LEGEND */}
            <div className="d-flex gap-4 mb-3 flex-wrap">
              {Object.keys(COLORS).map((key) => (
                <Legend
                  key={key}
                  label={
                    key === "mortgagePaid"
                      ? "Mortgage Paid"
                      : key === "rentPaid"
                      ? "Total Rent Paid"
                      : "Mortgage Outstanding"
                  }
                  color={COLORS[key]}
                  active={visible[key]}
                  onClick={() =>
                    setVisible((p) => ({
                      ...p,
                      [key]: !p[key],
                    }))
                  }
                />
              ))}
            </div>

            <LineChart
              height={300}
               margin={{ top: 60, right: 30, bottom: 50, left: 70 }}
              xAxis={[
                {
                  data: Array.from(
                    { length: loanYears + 1 },
                    (_, i) => i
                  ),
                  label: "Years",
                },
              ]}
              series={[
                visible.mortgagePaid && {
                  label: "Mortgage Paid",
                  data: calc.mortgagePaid,
                  area: true,
                    
                  color: COLORS.mortgagePaid,
                },
                visible.rentPaid && {
                  label: "Total Rent Paid",
                  data: calc.rentPaid,
                  area: true,
                 
                  color: COLORS.rentPaid,
                },
                visible.outstanding && {
                  label: "Mortgage Outstanding",
                  data: calc.outstanding,
                  area: true,
                 
                  color: COLORS.outstanding,
                },
              ].filter(Boolean)}
            />
            {/* <LineChart
  height={isMobile ? 220 : 300}
  margin={{ top: 40, right: 30, bottom: 50, left: 70 }}
  xAxis={[
    {
      data: Array.from({ length: loanYears + 1 }, (_, i) => i),
      label: "Years",
    },
  ]}
  series={[
    visible.mortgagePaid && {
      label: "Mortgage Paid",
      data: calc.mortgagePaid,
      area: true,
      showMark: !isMobile,
      color: COLORS.mortgagePaid,
    },
    visible.rentPaid && {
      label: "Total Rent Paid",
      data: calc.rentPaid,
      area: true,
      showMark: !isMobile,
      color: COLORS.rentPaid,
    },
    visible.outstanding && {
      label: "Mortgage Outstanding",
      data: calc.outstanding,
      area: true,
      showMark: !isMobile,
      color: COLORS.outstanding,
    },
  ].filter(Boolean)}
  slotProps={{
    legend: { hidden: true },   // ✅ REMOVE TOP LEGEND
  }}
/> */}
          </div>
        </Col>
      </Row>

      {/* ================= SUMMARY ================= */}
      <CostSummaryToggle
        monthlyRent={calc.rentMonthly}
        yearlyRent={yearlyRent}
        emi={calc.emi}
        yearlyMortgage={calc.yearlyMortgage}
        downPayment={downPayment}
        totalMortgage={calc.totalMortgage}
        stayYears={stayYears}
      />
    </Container>
  );
}

/* ================= LEGEND ================= */
function Legend({ label, color, active, onClick }) {
  return (
    <div
      onClick={onClick}
      className="d-flex align-items-center gap-2"
      style={{
        cursor: "pointer",
        opacity: active ? 1 : 0.4,
      }}
    >
      <div
        style={{
          width: 14,
          height: 14,
          backgroundColor: color,
          borderRadius: 3,
        }}
      />
      <span>{label}</span>
    </div>
  );
}
